import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Download,
  Visibility,
  VerifiedUser,
  Warning,
  Error,
  Info,
  Assessment,
  Security,
  Timeline,
  TrendingUp,
  TrendingDown,
  Person,
  Business,
  AccountBalance,
  Work
} from '@mui/icons-material';
import { useTranslation } from '../i18n';
import { KYCComplianceReport, KYCVerification, KYCAuditLog } from '../types';
import riskManagementApi from '../services/riskManagementApi';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar
} from 'recharts';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const KYCReports: React.FC = () => {
  const { t } = useTranslation();
  const [selectedPeriod, setSelectedPeriod] = useState<'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annual'>('monthly');
  const [selectedReport, setSelectedReport] = useState<KYCComplianceReport | null>(null);
  
  // Pending verifications modal state
  const [pendingVerificationsOpen, setPendingVerificationsOpen] = useState(false);
  const [pendingApplications, setPendingApplications] = useState<any[]>([]);
  const [loadingPending, setLoadingPending] = useState(false);
  
  // View application modal state
  const [viewApplicationOpen, setViewApplicationOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<any>(null);
  
  // Verification modal state
  const [verifyApplicationOpen, setVerifyApplicationOpen] = useState(false);
  const [verifyingApplication, setVerifyingApplication] = useState<any>(null);
  const [verificationNotes, setVerificationNotes] = useState('');

  // Mock KYC Compliance Report data
  const mockKYCComplianceReport: KYCComplianceReport = {
    id: '1',
    reportDate: '2024-01-15',
    period: 'monthly',
    summary: {
      totalApplications: 150,
      verifiedApplications: 120,
      pendingVerifications: 25,
      failedVerifications: 5,
      complianceRate: 80,
      averageVerificationTime: 48
    },
    verificationBreakdown: {
      identity: { total: 150, verified: 120, pending: 25, failed: 5 },
      address: { total: 150, verified: 115, pending: 30, failed: 5 },
      employment: { total: 150, verified: 110, pending: 35, failed: 5 },
      income: { total: 150, verified: 105, pending: 40, failed: 5 },
      bank: { total: 150, verified: 100, pending: 45, failed: 5 },
      business: { total: 50, verified: 40, pending: 8, failed: 2 }
    },
    riskDistribution: {
      low: 80,
      medium: 50,
      high: 20
    },
    complianceIssues: [
      {
        type: 'Document Verification',
        count: 15,
        severity: 'medium',
        description: 'Incomplete document submissions',
        recommendations: ['Implement document checklist', 'Add validation rules']
      },
      {
        type: 'Address Verification',
        count: 8,
        severity: 'low',
        description: 'Address mismatch with utility bills',
        recommendations: ['Verify address with multiple sources', 'Update address validation']
      },
      {
        type: 'Employment Verification',
        count: 12,
        severity: 'medium',
        description: 'Employment details not verified',
        recommendations: ['Implement employment verification API', 'Add manual verification process']
      }
    ],
    regulatoryUpdates: [
      {
        update: 'Enhanced KYC requirements for high-risk customers',
        impact: 'high',
        implementationDate: '2024-03-01',
        status: 'pending'
      },
      {
        update: 'Updated AML screening criteria',
        impact: 'medium',
        implementationDate: '2024-02-15',
        status: 'implemented'
      }
    ]
  };

  // Mock verification trends data
  const mockVerificationTrends = [
    { month: 'Jan', verified: 85, pending: 15, failed: 5 },
    { month: 'Feb', verified: 88, pending: 12, failed: 3 },
    { month: 'Mar', verified: 82, pending: 18, failed: 7 },
    { month: 'Apr', verified: 90, pending: 10, failed: 2 },
    { month: 'May', verified: 87, pending: 13, failed: 4 },
    { month: 'Jun', verified: 92, pending: 8, failed: 1 },
    { month: 'Jul', verified: 89, pending: 11, failed: 3 },
    { month: 'Aug', verified: 91, pending: 9, failed: 2 },
    { month: 'Sep', verified: 88, pending: 12, failed: 4 },
    { month: 'Oct', verified: 93, pending: 7, failed: 1 },
    { month: 'Nov', verified: 90, pending: 10, failed: 2 },
    { month: 'Dec', verified: 95, pending: 5, failed: 0 }
  ];

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'low':
        return 'success';
      case 'medium':
        return 'warning';
      case 'high':
        return 'error';
      default:
        return 'default';
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'low':
        return 'success';
      case 'medium':
        return 'warning';
      case 'high':
        return 'error';
      default:
        return 'default';
    }
  };

  // Function to fetch pending applications
  const fetchPendingApplications = async () => {
    try {
      setLoadingPending(true);
      const response = await riskManagementApi.getApplications({
        status: 'pending',
        limit: 50
      });
      setPendingApplications(response.data || []);
    } catch (error) {
      console.error('Error fetching pending applications:', error);
      // Fallback to mock data if API fails
      setPendingApplications([
        {
          id: 'app-001',
          applicantName: 'John Doe',
          email: 'john.doe@example.com',
          customerType: 'individual',
          requestedAmount: 300000000,
          monthlyIncome: 51000000,
          submissionDate: '2023-10-01T10:00:00Z',
          verificationStatus: 'pending',
          verificationType: 'KYC',
          pendingDocuments: ['identityProof', 'addressProof']
        },
        {
          id: 'app-002',
          applicantName: 'Jane Smith',
          email: 'jane.smith@example.com',
          customerType: 'individual',
          requestedAmount: 150000000,
          monthlyIncome: 27000000,
          submissionDate: '2023-10-05T11:30:00Z',
          verificationStatus: 'pending',
          verificationType: 'KYC',
          pendingDocuments: ['incomeProof', 'employmentVerification']
        },
        {
          id: 'app-003',
          applicantName: 'TechStart Solutions',
          email: 'sarah@techstart.com',
          customerType: 'sme',
          requestedAmount: 3000000000,
          submissionDate: '2023-10-12T14:20:00Z',
          verificationStatus: 'pending',
          verificationType: 'Business Verification',
          pendingDocuments: ['businessRegistration', 'financialStatements']
        }
      ]);
    } finally {
      setLoadingPending(false);
    }
  };

  // Function to handle pending verifications card click
  const handlePendingVerificationsClick = () => {
    fetchPendingApplications();
    setPendingVerificationsOpen(true);
  };

  // Function to handle view application
  const handleViewApplication = (application: any) => {
    setSelectedApplication(application);
    setViewApplicationOpen(true);
  };

  // Function to handle verify application
  const handleVerifyApplication = (application: any) => {
    setVerifyingApplication(application);
    setVerifyApplicationOpen(true);
  };

  // Function to submit verification
  const handleSubmitVerification = async () => {
    if (!verifyingApplication || !verificationNotes.trim()) return;
    
    try {
      // In a real implementation, this would call the backend API
      // For now, we'll just close the modal and show success
      console.log('Verification submitted:', {
        applicationId: verifyingApplication.id,
        notes: verificationNotes,
        status: 'verified',
        timestamp: new Date().toISOString()
      });
      
      // Close modal and refresh the list
      setVerifyApplicationOpen(false);
      setVerifyingApplication(null);
      setVerificationNotes('');
      
      // Refresh pending applications
      await fetchPendingApplications();
      
      // Show success message (you can add a toast notification here)
      alert('Application verified successfully!');
      
    } catch (error) {
      console.error('Error submitting verification:', error);
      alert('Error submitting verification. Please try again.');
    }
  };

  // Function to export pending list
  const handleExportPendingList = () => {
    try {
      // Prepare data for export
      const exportData = pendingApplications.map(app => ({
        'Application ID': app.id,
        'Applicant Name': app.applicantName || app.businessName,
        'Email': app.email,
        'Customer Type': app.customerType,
        'Requested Amount (ZiG)': app.requestedAmount,
        'Submission Date': new Date(app.submissionDate).toLocaleDateString(),
        'Verification Type': app.verificationType || 'KYC',
        'Pending Documents': (app.pendingDocuments || []).join(', '),
        'Status': 'Pending Verification'
      }));

      // Create Excel file
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Pending Verifications');
      
      // Generate filename with current date
      const filename = `Pending_Verifications_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, filename);
      
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Error exporting data. Please try again.');
    }
  };

  const exportKYCReportToXLS = () => {
    const reportData = {
      'Report Date': mockKYCComplianceReport.reportDate,
      'Period': mockKYCComplianceReport.period,
      'Total Applications': mockKYCComplianceReport.summary.totalApplications,
      'Verified Applications': mockKYCComplianceReport.summary.verifiedApplications,
      'Pending Verifications': mockKYCComplianceReport.summary.pendingVerifications,
      'Failed Verifications': mockKYCComplianceReport.summary.failedVerifications,
      'Compliance Rate': `${mockKYCComplianceReport.summary.complianceRate}%`,
      'Average Verification Time': `${mockKYCComplianceReport.summary.averageVerificationTime} hours`
    };

    const worksheet = XLSX.utils.json_to_sheet([reportData]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'KYC Compliance Report');
    XLSX.writeFile(workbook, `KYC_Compliance_Report_${selectedPeriod}_${mockKYCComplianceReport.reportDate}.xlsx`);
  };

  const exportKYCReportToPDF = () => {
    const doc = new jsPDF();
    
    // Add title
    doc.setFontSize(20);
    doc.text('KYC Compliance Report', 20, 20);
    
    // Add report details
    doc.setFontSize(12);
    doc.text(`Report Date: ${mockKYCComplianceReport.reportDate}`, 20, 40);
    doc.text(`Period: ${mockKYCComplianceReport.period}`, 20, 50);
    doc.text(`Total Applications: ${mockKYCComplianceReport.summary.totalApplications}`, 20, 60);
    doc.text(`Verified Applications: ${mockKYCComplianceReport.summary.verifiedApplications}`, 20, 70);
    doc.text(`Compliance Rate: ${mockKYCComplianceReport.summary.complianceRate}%`, 20, 80);
    
    // Add verification breakdown table
    const breakdownData = Object.entries(mockKYCComplianceReport.verificationBreakdown).map(([type, data]) => [
      type.charAt(0).toUpperCase() + type.slice(1),
      data.total.toString(),
      data.verified.toString(),
      data.pending.toString(),
      data.failed.toString()
    ]);
    
    autoTable(doc, {
      head: [['Verification Type', 'Total', 'Verified', 'Pending', 'Failed']],
      body: breakdownData,
      startY: 100,
      theme: 'grid'
    });
    
    doc.save(`KYC_Compliance_Report_${selectedPeriod}_${mockKYCComplianceReport.reportDate}.pdf`);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        KYC Compliance Reports
      </Typography>

      {/* KYC Overview Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <VerifiedUser color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Compliance Rate</Typography>
            </Box>
            <Typography variant="h3" color="primary">
              {mockKYCComplianceReport.summary.complianceRate}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Overall KYC compliance
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Person color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Total Applications</Typography>
            </Box>
            <Typography variant="h3" color="primary">
              {mockKYCComplianceReport.summary.totalApplications}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Applications processed
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Timeline color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Verification Time</Typography>
            </Box>
            <Typography variant="h3" color="primary">
              {mockKYCComplianceReport.summary.averageVerificationTime}h
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Average processing time
            </Typography>
          </CardContent>
        </Card>

        <Card 
          sx={{ 
            minWidth: 200, 
            flex: 1, 
            cursor: 'pointer',
            '&:hover': {
              boxShadow: 4,
              transform: 'translateY(-2px)',
              transition: 'all 0.2s ease-in-out'
            }
          }}
          onClick={handlePendingVerificationsClick}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Warning color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Pending Verifications</Typography>
            </Box>
            <Typography variant="h3" color="primary">
              {mockKYCComplianceReport.summary.pendingVerifications}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Awaiting verification
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Click to view details
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Filters and Export */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Report Period</InputLabel>
          <Select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value as any)}
            label={t('kyc.reportPeriod')}
          >
            <MenuItem value="daily">Daily</MenuItem>
            <MenuItem value="weekly">Weekly</MenuItem>
            <MenuItem value="monthly">Monthly</MenuItem>
            <MenuItem value="quarterly">Quarterly</MenuItem>
            <MenuItem value="annual">Annual</MenuItem>
          </Select>
        </FormControl>
        
        <Button
          variant="contained"
          startIcon={<Download />}
          onClick={exportKYCReportToXLS}
        >
          Export to Excel
        </Button>
        
        <Button
          variant="outlined"
          startIcon={<Download />}
          onClick={exportKYCReportToPDF}
        >
          Export to PDF
        </Button>
      </Box>

      {/* Charts Section */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
        {/* Verification Trends */}
        <Paper sx={{ p: 2, flex: 1, minWidth: 500 }}>
          <Typography variant="h6" gutterBottom>
            Verification Trends
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={mockVerificationTrends}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <RechartsTooltip />
              <Line
                type="monotone"
                dataKey="verified"
                stroke="#4caf50"
                strokeWidth={2}
                name={t('kyc.verified')}
              />
              <Line
                type="monotone"
                dataKey="pending"
                stroke="#ff9800"
                strokeWidth={2}
                name={t('kyc.pending')}
              />
              <Line
                type="monotone"
                dataKey="failed"
                stroke="#f44336"
                strokeWidth={2}
                name={t('kyc.failed')}
              />
            </LineChart>
          </ResponsiveContainer>
        </Paper>

        {/* Risk Distribution */}
        <Paper sx={{ p: 2, flex: 1, minWidth: 400 }}>
          <Typography variant="h6" gutterBottom>
            Risk Distribution
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={[
                  { name: 'Low Risk', value: mockKYCComplianceReport.riskDistribution.low, color: '#4caf50' },
                  { name: 'Medium Risk', value: mockKYCComplianceReport.riskDistribution.medium, color: '#ff9800' },
                  { name: 'High Risk', value: mockKYCComplianceReport.riskDistribution.high, color: '#f44336' }
                ]}
                cx="50%"
                cy="50%"
                outerRadius={80}
                dataKey="value"
                label={({ name, value }) => `${name}: ${value}`}
              >
                {[
                  { name: 'Low Risk', value: mockKYCComplianceReport.riskDistribution.low, color: '#4caf50' },
                  { name: 'Medium Risk', value: mockKYCComplianceReport.riskDistribution.medium, color: '#ff9800' },
                  { name: 'High Risk', value: mockKYCComplianceReport.riskDistribution.high, color: '#f44336' }
                ].map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <RechartsTooltip />
            </PieChart>
          </ResponsiveContainer>
        </Paper>
      </Box>

      {/* Verification Breakdown Table */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">
            Verification Breakdown
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Verification Type</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Verified</TableCell>
                <TableCell>Pending</TableCell>
                <TableCell>Failed</TableCell>
                <TableCell>Success Rate</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(mockKYCComplianceReport.verificationBreakdown).map(([type, data]) => (
                <TableRow key={type}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {type === 'identity' && <Person sx={{ mr: 1 }} />}
                      {type === 'address' && <Business sx={{ mr: 1 }} />}
                      {type === 'employment' && <Work sx={{ mr: 1 }} />}
                      {type === 'income' && <AccountBalance sx={{ mr: 1 }} />}
                      {type === 'bank' && <AccountBalance sx={{ mr: 1 }} />}
                      {type === 'business' && <Business sx={{ mr: 1 }} />}
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                        {type}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{data.total}</TableCell>
                  <TableCell>
                    <Chip
                      label={data.verified}
                      color="success"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={data.pending}
                      color="warning"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={data.failed}
                      color="error"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="primary">
                      {Math.round((data.verified / data.total) * 100)}%
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Compliance Issues */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">
            Compliance Issues
          </Typography>
        </Box>
        <Box sx={{ p: 2 }}>
          {mockKYCComplianceReport.complianceIssues.map((issue, index) => (
            <Box key={index} sx={{ mb: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  {issue.type}
                </Typography>
                <Chip
                  label={issue.severity}
                  color={getSeverityColor(issue.severity) as any}
                  size="small"
                />
              </Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Count: {issue.count} • {issue.description}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Recommendations:</strong> {issue.recommendations.join(', ')}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Regulatory Updates */}
      <Paper>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">
            Regulatory Updates
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Update</TableCell>
                <TableCell>Impact</TableCell>
                <TableCell>Implementation Date</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mockKYCComplianceReport.regulatoryUpdates.map((update, index) => (
                <TableRow key={index}>
                  <TableCell>{update.update}</TableCell>
                  <TableCell>
                    <Chip
                      label={update.impact}
                      color={getImpactColor(update.impact) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{update.implementationDate}</TableCell>
                  <TableCell>
                    <Chip
                      label={update.status}
                      color={update.status === 'implemented' ? 'success' : update.status === 'pending' ? 'warning' : 'error'}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Pending Verifications Modal */}
      <Dialog
        open={pendingVerificationsOpen}
        onClose={() => setPendingVerificationsOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              Pending Verifications - {pendingApplications.length} Applications
            </Typography>
            <IconButton onClick={() => setPendingVerificationsOpen(false)}>
              <Typography variant="h6">×</Typography>
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingPending ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <Typography>Loading pending applications...</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Applicant</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Submission Date</TableCell>
                    <TableCell>Verification Type</TableCell>
                    <TableCell>Pending Documents</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingApplications.map((app, index) => (
                    <TableRow key={app.id || index}>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {app.applicantName || app.businessName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {app.email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={app.customerType}
                          color={app.customerType === 'sme' ? 'primary' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {(app.requestedAmount / 1000000).toFixed(1)}M ZiG
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(app.submissionDate).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {app.verificationType || 'KYC'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {(app.pendingDocuments || []).map((doc: string, docIndex: number) => (
                            <Chip
                              key={docIndex}
                              label={doc}
                              size="small"
                              variant="outlined"
                              color="warning"
                            />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Visibility />}
                            onClick={() => handleViewApplication(app)}
                          >
                            View
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            onClick={() => handleVerifyApplication(app)}
                          >
                            Verify
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingVerificationsOpen(false)}>
            Close
          </Button>
          <Button variant="contained" color="primary" onClick={handleExportPendingList}>
            Export Pending List
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Application Modal */}
      <Dialog
        open={viewApplicationOpen}
        onClose={() => setViewApplicationOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              Application Details
            </Typography>
            <IconButton onClick={() => setViewApplicationOpen(false)}>
              <Typography variant="h6">×</Typography>
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedApplication && (
            <Box sx={{ mt: 2 }}>
              {/* Basic Information */}
              <Typography variant="h6" gutterBottom>Basic Information</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Applicant Name</Typography>
                  <Typography variant="body1">{selectedApplication.applicantName || selectedApplication.businessName}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                  <Typography variant="body1">{selectedApplication.email}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Customer Type</Typography>
                  <Chip
                    label={selectedApplication.customerType}
                    color={selectedApplication.customerType === 'sme' ? 'primary' : 'default'}
                    size="small"
                  />
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                  <Typography variant="body1">{selectedApplication.phone || 'N/A'}</Typography>
                </Box>
              </Box>

              {/* Loan Information */}
              <Typography variant="h6" gutterBottom>Loan Information</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Requested Amount</Typography>
                  <Typography variant="body1">{(selectedApplication.requestedAmount / 1000000).toFixed(1)}M ZiG</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Submission Date</Typography>
                  <Typography variant="body1">{new Date(selectedApplication.submissionDate).toLocaleDateString()}</Typography>
                </Box>
                {selectedApplication.monthlyIncome && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Monthly Income</Typography>
                    <Typography variant="body1">{(selectedApplication.monthlyIncome / 1000000).toFixed(1)}M ZiG</Typography>
                  </Box>
                )}
                {selectedApplication.yearsInBusiness && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Years in Business</Typography>
                    <Typography variant="body1">{selectedApplication.yearsInBusiness} years</Typography>
                  </Box>
                )}
              </Box>

              {/* Verification Details */}
              <Typography variant="h6" gutterBottom>Verification Details</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Verification Type</Typography>
                  <Typography variant="body1">{selectedApplication.verificationType || 'KYC'}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Chip
                    label="Pending Verification"
                    color="warning"
                    size="small"
                  />
                </Box>
              </Box>

              {/* Pending Documents */}
              <Typography variant="h6" gutterBottom>Pending Documents</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {(selectedApplication.pendingDocuments || []).map((doc: string, index: number) => (
                  <Chip
                    key={index}
                    label={doc}
                    size="small"
                    variant="outlined"
                    color="warning"
                  />
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewApplicationOpen(false)}>
            Close
          </Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => {
              setViewApplicationOpen(false);
              if (selectedApplication) {
                handleVerifyApplication(selectedApplication);
              }
            }}
          >
            Proceed to Verification
          </Button>
        </DialogActions>
      </Dialog>

      {/* Verify Application Modal */}
      <Dialog
        open={verifyApplicationOpen}
        onClose={() => setVerifyApplicationOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              Verify Application - {verifyingApplication?.applicantName || verifyingApplication?.businessName}
            </Typography>
            <IconButton onClick={() => setVerifyApplicationOpen(false)}>
              <Typography variant="h6">×</Typography>
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {verifyingApplication && (
            <Box sx={{ mt: 2 }}>
              {/* Application Summary */}
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>Application Summary</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Amount Requested</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {(verifyingApplication.requestedAmount / 1000000).toFixed(1)}M ZiG
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Customer Type</Typography>
                    <Typography variant="body1">{verifyingApplication.customerType}</Typography>
                  </Box>
                </Box>
              </Box>

              {/* Verification Notes */}
              <Typography variant="subtitle1" gutterBottom>Verification Notes</Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="Enter verification notes, findings, or any additional information..."
                value={verificationNotes}
                onChange={(e) => setVerificationNotes(e.target.value)}
                variant="outlined"
                sx={{ mb: 3 }}
              />

              {/* Verification Checklist */}
              <Typography variant="subtitle1" gutterBottom>Verification Checklist</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[
                  'Identity documents verified',
                  'Address proof confirmed',
                  'Income verification completed',
                  'Employment details validated',
                  'Bank account information verified',
                  'KYC compliance confirmed'
                ].map((item, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <input type="checkbox" id={`check-${index}`} />
                    <label htmlFor={`check-${index}`}>
                      <Typography variant="body2">{item}</Typography>
                    </label>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerifyApplicationOpen(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleSubmitVerification}
            disabled={!verificationNotes.trim()}
          >
            Submit Verification
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KYCReports;
